"use client";

/**
 * The website footer.
 *
 * The landing page used to carry the only footer on the site, with three short columns,
 * and every other marketing page had none at all: /how-it-works ended on a call to action
 * and then simply stopped, with no way out except the back button. A site with fourteen
 * pages needs one footer that lists all of them, on all of them.
 *
 * Separate from components/layout/footer.tsx, which is the quiet utility row inside the
 * signed-in product. That one is deliberately one line: most app screens are shorter than
 * the viewport, so a tall marketing footer would be permanently in shot.
 *
 * The registered company block is not decoration. The Companies Act and the e-commerce
 * regulations want the registered name, number, place of registration and registered
 * office somewhere a customer can find them. Verified on Companies House 2026-08-12:
 * GrowthSync Network Ltd, 16795645, active, Unit 6 Balfour Court. There is a separate,
 * unrelated GROWTHSYNC LTD (16292576) in London, so match on the address before ever
 * editing these values.
 */

import Link from "next/link";
import { useTranslations } from "@/hooks/use-translations";

const IOS_APP_STORE_URL =
  process.env.NEXT_PUBLIC_IOS_APP_STORE_URL || "https://apps.apple.com/app/growthsync/id6756941263";
const ANDROID_PLAY_STORE_URL =
  process.env.NEXT_PUBLIC_ANDROID_PLAY_STORE_URL ||
  "https://play.google.com/store/apps/details?id=com.growthsync.network";

type Column = { titleKey: string; links: { href: string; labelKey: string }[] };

const COLUMNS: Column[] = [
  {
    titleKey: "footer.product",
    links: [
      { href: "/features/discover", labelKey: "features.discover.name" },
      { href: "/features/events", labelKey: "features.events.name" },
      { href: "/features/communities", labelKey: "features.communities.name" },
      { href: "/features/opportunities", labelKey: "features.opportunities.name" },
      { href: "/features/reputation", labelKey: "features.reputation.name" },
      { href: "/pricing", labelKey: "nav.pricing" },
    ],
  },
  {
    titleKey: "footer.audiences",
    links: [
      { href: "/for/founders", labelKey: "audiences.founders.name" },
      { href: "/for/business-owners", labelKey: "audiences.owners.name" },
      { href: "/networking-for-entrepreneurs", labelKey: "landing:networkingForEntrepreneurs" },
      { href: "/meet-business-partners", labelKey: "landing:meetBusinessPartners" },
      { href: "/business-networking-app", labelKey: "landing:businessNetworkingApp" },
    ],
  },
  {
    titleKey: "footer.company",
    links: [
      { href: "/about", labelKey: "company.about" },
      { href: "/how-it-works", labelKey: "company.howItWorks" },
      { href: "/faq", labelKey: "company.faq" },
      { href: "/safety", labelKey: "company.safety" },
      { href: "/contact", labelKey: "company.contact" },
      { href: "/get-app", labelKey: "company.getApp" },
    ],
  },
  {
    titleKey: "footer.legal",
    links: [
      { href: "/terms", labelKey: "landing:terms" },
      { href: "/privacy", labelKey: "landing:privacy" },
      { href: "/cookies", labelKey: "site:footer.cookies" },
      { href: "/child-safety", labelKey: "landing:childSafety" },
      { href: "/account-deletion", labelKey: "site:footer.accountDeletion" },
    ],
  },
];

export function SiteFooter() {
  const { t } = useTranslations("site");
  const { t: tLanding } = useTranslations("landing");

  /* Most footer labels already exist in the landing namespace, translated into all 21
     locales. Reusing them rather than minting "site.footer.terms" keeps a single string
     per concept, so "Privacy" cannot end up saying two different things in Polish.
     A key may name its namespace with a "landing:" or "site:" prefix; bare keys are site. */
  const label = (key: string) => {
    const [namespace, rest] = key.includes(":") ? key.split(":") : ["site", key];
    return namespace === "landing" ? tLanding(rest) : t(rest);
  };

  return (
    <footer className="border-t border-neutral-200 bg-[#f6f8fc]">
      <div className="gs-page py-14 md:py-20">
        <div className="grid gap-10 md:grid-cols-[minmax(0,1.1fr)_minmax(0,2.4fr)] md:gap-12">
          <div className="min-w-0">
            <p className="text-lg font-bold tracking-[-0.02em] text-neutral-900">GrowthSync</p>
            <p className="mt-3 max-w-[34ch] text-sm leading-relaxed text-neutral-600">{t("footer.tagline")}</p>

            <p className="mt-8 gs-eyebrow text-neutral-600">{t("footer.getTheApp")}</p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <a href={IOS_APP_STORE_URL} target="_blank" rel="noopener" aria-label={t("footer.appStore")}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/badges/app-store-badge.svg" alt={t("footer.appStore")} width={120} height={40} className="h-10 w-auto" loading="lazy" />
              </a>
              <a href={ANDROID_PLAY_STORE_URL} target="_blank" rel="noopener" aria-label={t("footer.googlePlay")}>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src="/badges/google-play-badge.png" alt={t("footer.googlePlay")} width={135} height={46} className="h-[46px] w-auto" loading="lazy" />
              </a>
            </div>
          </div>

          <div className="grid min-w-0 grid-cols-2 gap-x-6 gap-y-9 sm:grid-cols-4 sm:gap-x-8">
            {COLUMNS.map((column) => (
              <nav key={column.titleKey} aria-label={t(column.titleKey)} className="min-w-0">
                <p className="gs-eyebrow text-neutral-600">{t(column.titleKey)}</p>
                <ul className="mt-3 space-y-1">
                  {column.links.map((link) => (
                    <li key={link.href}>
                      <Link
                        href={link.href}
                        className="inline-flex min-h-[2.25rem] items-center text-[13.5px] leading-snug text-neutral-600 transition-colors hover:text-neutral-900"
                      >
                        {label(link.labelKey)}
                      </Link>
                    </li>
                  ))}
                </ul>
              </nav>
            ))}
          </div>
        </div>

        <div className="mt-12 flex flex-col gap-4 border-t border-neutral-200 pt-8 sm:flex-row sm:items-start sm:justify-between sm:gap-8">
          <p className="min-w-0 flex-1 text-xs leading-relaxed text-neutral-600">
            © <span suppressHydrationWarning>{new Date().getFullYear()}</span> GrowthSync Network Ltd. {tLanding("allRightsReserved")}{" "}
            Registered in England and Wales, company number 16795645. Registered office: Unit 6 Balfour Court, Leyland, PR25 2TF.
          </p>
          <a
            href="https://www.ownlydigital.co.uk/work/growthsync?ref=growthsync"
            target="_blank"
            rel="noopener"
            className="inline-flex shrink-0 items-center text-xs text-neutral-600 transition-colors hover:text-neutral-900"
          >
            {tLanding("builtByOwnly")}
          </a>
        </div>
      </div>
    </footer>
  );
}
